import {
  getCachedImage,
  queuePrefetchImageToCache,
} from '@/lib/storage/image-cache';
import { useEffect, useRef, useState } from 'react';

type UseCachedImageOptions = {
  enabled?: boolean;
  prefetch?: 'off' | 'idle';
  activateCacheRead?: boolean;
  preferCacheRead?: boolean;
};

type IdleCallbackHandle = number;
type IdleCallback = (deadline: { timeRemaining: () => number }) => void;

const FALLBACK_IDLE_DELAY_MS = 150;

function runWhenIdle(callback: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }
  const idleWindow = window as Window & {
    requestIdleCallback?: (cb: IdleCallback) => IdleCallbackHandle;
    cancelIdleCallback?: (id: IdleCallbackHandle) => void;
  };
  if (typeof idleWindow.requestIdleCallback === 'function') {
    const id = idleWindow.requestIdleCallback(() => callback());
    return () => {
      if (typeof idleWindow.cancelIdleCallback === 'function') {
        idleWindow.cancelIdleCallback(id);
      }
    };
  }
  const timeout = window.setTimeout(callback, FALLBACK_IDLE_DELAY_MS);
  return () => window.clearTimeout(timeout);
}

export function useCachedImage(
  url?: string | null,
  options: UseCachedImageOptions = {}
) {
  const {
    enabled = true,
    prefetch = 'off',
    activateCacheRead = true,
    preferCacheRead = false,
  } = options;
  const [src, setSrc] = useState<string | null>(null);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [isResolved, setIsResolved] = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let cancelIdleTask: (() => void) | null = null;

    const cleanupObjectUrl = () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };

    const scheduleIdlePrefetch = () => {
      if (prefetch !== 'idle' || !url) return;
      cancelIdleTask = runWhenIdle(() => {
        if (cancelled) return;
        void queuePrefetchImageToCache(url, { priority: 'low' });
      });
    };

    if (!url) {
      cleanupObjectUrl();
      setSourceUrl(null);
      setSrc(null);
      setIsResolved(false);
      setFromCache(false);
      return cleanupObjectUrl;
    }

    if (!enabled) {
      cleanupObjectUrl();
      setSourceUrl(url);
      setSrc(url);
      setIsResolved(true);
      setFromCache(false);
      return cleanupObjectUrl;
    }

    if (!activateCacheRead) {
      cleanupObjectUrl();
      setSourceUrl(url);
      setSrc(url);
      setIsResolved(true);
      setFromCache(false);
      scheduleIdlePrefetch();
      return () => {
        cancelled = true;
        cancelIdleTask?.();
        cleanupObjectUrl();
      };
    }

    cleanupObjectUrl();
    setSourceUrl(url);
    if (preferCacheRead) {
      setSrc(null);
      setIsResolved(false);
      setFromCache(false);
    } else {
      setSrc(url);
      setIsResolved(true);
      setFromCache(false);
    }

    const load = async () => {
      try {
        const cached = await getCachedImage(url);
        if (cancelled) return;
        if (cached?.blob) {
          cleanupObjectUrl();
          const objectUrl = URL.createObjectURL(cached.blob);
          objectUrlRef.current = objectUrl;
          setSourceUrl(url);
          setSrc(objectUrl);
          setFromCache(true);
          setIsResolved(true);
          return;
        }
      } catch {
        // Cache read failures should not block rendering.
      }

      if (!cancelled) {
        setSourceUrl(url);
        setSrc(url);
        setFromCache(false);
        setIsResolved(true);
      }
      scheduleIdlePrefetch();
    };

    void load();

    return () => {
      cancelled = true;
      cancelIdleTask?.();
      cleanupObjectUrl();
    };
  }, [activateCacheRead, enabled, prefetch, preferCacheRead, url]);

  const isCurrentSource = sourceUrl === (url ?? null);

  return {
    src: isCurrentSource ? src : null,
    isResolved: isCurrentSource && isResolved,
    fromCache: isCurrentSource && fromCache,
  };
}
